import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useParams } from '@tanstack/react-router';
import {
  useGithubInstallation,
  useGithubRepositories,
  useProject,
  useProjectMemberRates,
  useSetProjectMemberRate,
  useUpdateProject,
  useWorkspace,
  useMyWorkspaceMembership,
  useWorkspaceMembers,
  type ProjectBillingType,
  type ProjectType,
} from '@fluxter/shared';
import { toast } from 'sonner';
import { Check, ExternalLink } from 'lucide-react';
import { ProjectPageHeader } from '@/components/ProjectPageHeader';
import { SkeletonStack } from '@/components/Skeletons';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

interface ProjectAttrs {
  name?: string;
  description?: string | null;
  type?: ProjectType;
  github_repo_owner?: string | null;
  github_repo_name?: string | null;
  lead_user_id?: string | null;
  billing_type?: ProjectBillingType;
  flat_fee_cents?: number | null;
  default_hourly_rate_cents?: number | null;
}

interface MemberAttrs {
  name?: string;
  email?: string;
  role?: 'owner' | 'admin' | 'member';
}

interface MemberItem {
  id: string;
  attributes?: MemberAttrs;
}

interface MemberRateAttrs {
  user_id?: string;
  hourly_rate_cents?: number;
}

interface MemberRateItem {
  id: string;
  attributes?: MemberRateAttrs;
}

const UNASSIGNED_VALUE = '__unassigned__';

const centsToInputValue = (cents: number | null | undefined): string => {
  if (cents === null || cents === undefined) return '';
  return (cents / 100).toFixed(2);
};

const inputValueToCents = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number.parseFloat(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.round(parsed * 100);
};

export const ProjectSettingsPage = () => {
  const { workspaceSlug, projectSlug } = useParams({
    from: '/w/$workspaceSlug/p/$projectSlug/settings',
  });
  const workspace = useWorkspace(workspaceSlug);
  const project = useProject(workspaceSlug, projectSlug);
  const members = useWorkspaceMembers(workspaceSlug);
  const memberRates = useProjectMemberRates(workspaceSlug, projectSlug);
  const membership = useMyWorkspaceMembership(workspaceSlug);

  const update = useUpdateProject(workspaceSlug, projectSlug);

  const memberList = (members.data as { data?: MemberItem[] } | undefined)?.data ?? [];
  const isOwner = membership.isOwner;

  const projectAttrs = (project.data as { data?: { attributes?: ProjectAttrs } } | undefined)?.data
    ?.attributes;
  const workspaceName = (workspace.data as { data?: { attributes?: { name?: string } } } | undefined)
    ?.data?.attributes?.name;

  // --- Detail form state ---
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [projectType, setProjectType] = useState<ProjectType>('generic');
  const [leadUserId, setLeadUserId] = useState('');
  const [detailError, setDetailError] = useState<string | null>(null);

  // --- Billing form state ---
  const [billingType, setBillingType] = useState<ProjectBillingType>('none');
  const [flatFee, setFlatFee] = useState('');
  const [defaultRate, setDefaultRate] = useState('');
  const [billingError, setBillingError] = useState<string | null>(null);
  const [billingDirty, setBillingDirty] = useState(false);

  useEffect(() => {
    if (!projectAttrs) return;
    setName(projectAttrs.name ?? '');
    setDescription(projectAttrs.description ?? '');
    setProjectType(projectAttrs.type ?? 'generic');
    setLeadUserId(projectAttrs.lead_user_id ?? '');
    setBillingType(projectAttrs.billing_type ?? 'none');
    setFlatFee(centsToInputValue(projectAttrs.flat_fee_cents));
    setDefaultRate(centsToInputValue(projectAttrs.default_hourly_rate_cents));
    setBillingDirty(false);
  }, [projectAttrs]);

  const handleDetailSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setDetailError(null);

    /*
      Demoting an engineering project back to `generic` while it has a
      linked GitHub repo is rejected server-side (slice 11 will enforce
      the CHECK constraint). Surface a guard here too so the user gets
      a clear "unlink first" message instead of a generic 422.
    */
    if (
      projectType === 'generic'
      && projectAttrs?.type === 'engineering'
      && projectAttrs.github_repo_owner
    ) {
      setDetailError(
        'Unlink the GitHub repository before changing this project to "generic".',
      );
      return;
    }

    try {
      await update.mutateAsync({
        name: name.trim(),
        description: description.trim() === '' ? null : description,
        type: projectType,
        lead_user_id: leadUserId === '' ? null : leadUserId,
      });
    } catch (e) {
      setDetailError(e instanceof Error ? e.message : 'Could not save project.');
    }
  };

  const handleBillingSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBillingError(null);
    const flatFeeCents = inputValueToCents(flatFee);
    const defaultRateCents = inputValueToCents(defaultRate);

    if (billingType === 'flat' && flatFeeCents === null) {
      setBillingError('Flat-fee billing requires a non-zero flat fee.');
      return;
    }

    try {
      await update.mutateAsync({
        billing_type: billingType,
        flat_fee_cents: billingType === 'flat' ? flatFeeCents : null,
        default_hourly_rate_cents: billingType === 'hourly' ? defaultRateCents : null,
      });
      setBillingDirty(false);
    } catch (e) {
      setBillingError(e instanceof Error ? e.message : 'Could not save billing config.');
    }
  };

  if (project.isLoading || workspace.isLoading) {
    return (
      <>
        <SkeletonStack lines={8} />
      </>
    );
  }

  if (project.isError || !projectAttrs) {
    return (
      <>
        <p className="text-foreground-faint text-[12.5px]">Project not found.</p>
      </>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <ProjectPageHeader
          workspaceSlug={workspaceSlug}
          workspaceName={workspaceName}
          projectSlug={projectSlug}
          projectName={projectAttrs.name}
          current="settings"
        />

        <div>
          <h1 className="text-foreground text-[28px] font-semibold tracking-[-0.025em]">Project settings</h1>
          <p className="text-foreground-faint text-[12.5px]">
            Manage project details, billing configuration, and per-member rates.
          </p>
        </div>

        {/* --- Details ---------------------------------------------------- */}
        <section className="space-y-4 rounded-xl border border-[var(--glass-card-border)] bg-[var(--glass-card-bg)] p-6 shadow-[var(--glass-card-shadow)] backdrop-blur-md">
          <header>
            <h2 className="text-foreground text-[14px] font-semibold tracking-[-0.005em]">Details</h2>
            <p className="text-foreground-faint text-[12.5px]">Name, description, and project lead.</p>
          </header>

          <form onSubmit={handleDetailSave} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="project-name">Name</Label>
              <Input
                id="project-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="project-description">Description</Label>
              <Textarea
                id="project-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Markdown is fine."
              />
            </div>

            {/*
              Project type — `engineering` unlocks GitHub integration UI
              surfaces (slices 11/12) and the per-task type picker.
              `generic` is the default for non-dev work (HR, marketing,
              ops, etc.). Adding more types later is non-breaking. See
              `app/PRODUCT.md` § "Project type — feature gating".
            */}
            <div className="space-y-2">
              <Label htmlFor="project-type">Type</Label>
              <Select
                value={projectType}
                onValueChange={(next) => setProjectType(next as ProjectType)}
              >
                <SelectTrigger id="project-type" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="generic">
                    <span className="flex flex-col items-start gap-0.5">
                      <span>Generic</span>
                      <span className="text-foreground-faint text-[11.5px]">
                        Standard project — tasks, board, calendar, notes, time.
                      </span>
                    </span>
                  </SelectItem>
                  <SelectItem value="engineering">
                    <span className="flex flex-col items-start gap-0.5">
                      <span>Engineering</span>
                      <span className="text-foreground-faint text-[11.5px]">
                        Adds GitHub repo linking, task types, branch creation.
                      </span>
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="project-lead">Lead</Label>
              <Select
                value={leadUserId === '' ? UNASSIGNED_VALUE : leadUserId}
                onValueChange={(next) => setLeadUserId(next === UNASSIGNED_VALUE ? '' : next)}
              >
                <SelectTrigger id="project-lead" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNASSIGNED_VALUE}>No lead</SelectItem>
                  {memberList.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.attributes?.name ?? m.attributes?.email ?? m.id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {detailError ? (
              <p className="text-destructive text-sm" role="alert">
                {detailError}
              </p>
            ) : null}

            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? 'Saving…' : 'Save details'}
            </Button>
          </form>
        </section>

        {/* --- Repository (engineering only, slice 11) ------------------ */}
        {projectAttrs?.type === 'engineering' ? (
          <RepositorySection
            workspaceSlug={workspaceSlug}
            projectSlug={projectSlug}
            currentOwner={projectAttrs?.github_repo_owner ?? null}
            currentName={projectAttrs?.github_repo_name ?? null}
          />
        ) : null}

        {/* --- Billing --------------------------------------------------- */}
        <section className="space-y-4 rounded-xl border border-[var(--glass-card-border)] bg-[var(--glass-card-bg)] p-6 shadow-[var(--glass-card-shadow)] backdrop-blur-md">
          <header className="flex items-start justify-between gap-2">
            <div>
              <h2 className="text-foreground text-[14px] font-semibold tracking-[-0.005em]">Billing</h2>
              <p className="text-foreground-faint text-[12.5px]">
                {isOwner
                  ? 'Pick a billing model. Per-member overrides are managed below.'
                  : 'Read-only — only the workspace owner can change billing config.'}
              </p>
            </div>
          </header>

          <form onSubmit={handleBillingSave} className="space-y-4">
            <div className="space-y-2">
              <Label>Billing type</Label>
              <div className="flex flex-wrap gap-2">
                {(['none', 'flat', 'hourly'] as const).map((type) => (
                  <button
                    key={type}
                    type="button"
                    disabled={!isOwner}
                    onClick={() => {
                      setBillingType(type);
                      setBillingDirty(true);
                    }}
                    className={cn(
                      'rounded-md border px-3 py-1.5 text-sm transition-colors',
                      billingType === type
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'bg-background hover:bg-muted',
                      !isOwner && 'cursor-not-allowed opacity-60',
                    )}
                  >
                    {type === 'none' ? 'Non-billable' : type === 'flat' ? 'Flat fee' : 'Hourly'}
                  </button>
                ))}
              </div>
            </div>

            {billingType === 'flat' ? (
              <div className="space-y-2">
                <Label htmlFor="flat-fee">Flat fee</Label>
                <div className="flex items-center gap-2">
                  <span className="text-foreground-faint text-[12.5px]">$</span>
                  <Input
                    id="flat-fee"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={flatFee}
                    onChange={(e) => {
                      setFlatFee(e.target.value);
                      setBillingDirty(true);
                    }}
                    disabled={!isOwner}
                    placeholder="2500.00"
                    className="max-w-40"
                  />
                </div>
                <p className="text-foreground-faint text-[11.5px]">
                  Hours can still be tracked on flat-fee projects, but won&apos;t multiply against an
                  hourly rate.
                </p>
              </div>
            ) : null}

            {billingType === 'hourly' ? (
              <div className="space-y-2">
                <Label htmlFor="default-rate">Default hourly rate</Label>
                <div className="flex items-center gap-2">
                  <span className="text-foreground-faint text-[12.5px]">$</span>
                  <Input
                    id="default-rate"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={defaultRate}
                    onChange={(e) => {
                      setDefaultRate(e.target.value);
                      setBillingDirty(true);
                    }}
                    disabled={!isOwner}
                    placeholder="125.00"
                    className="max-w-40"
                  />
                  <span className="text-foreground-faint text-[12.5px]">/ hour</span>
                </div>
                <p className="text-foreground-faint text-[11.5px]">
                  Used for any member without a per-project override. If left empty, members must each
                  have an override before they can log time.
                </p>
              </div>
            ) : null}

            {billingError ? (
              <p className="text-destructive text-sm" role="alert">
                {billingError}
              </p>
            ) : null}

            {isOwner ? (
              <Button type="submit" disabled={update.isPending || !billingDirty}>
                {update.isPending ? 'Saving…' : 'Save billing'}
              </Button>
            ) : null}
          </form>
        </section>

        {/* --- Per-member rates ----------------------------------------- */}
        {billingType === 'hourly' ? (
          <MemberRatesSection
            workspaceSlug={workspaceSlug}
            projectSlug={projectSlug}
            members={memberList}
            rates={(memberRates.data as { data?: MemberRateItem[] } | undefined)?.data ?? []}
            isLoading={memberRates.isLoading}
            isOwner={isOwner}
          />
        ) : null}
      </div>
    </>
  );
};

interface MemberRatesSectionProps {
  workspaceSlug: string;
  projectSlug: string;
  members: MemberItem[];
  rates: MemberRateItem[];
  isLoading: boolean;
  isOwner: boolean;
}

const MemberRatesSection = ({
  workspaceSlug,
  projectSlug,
  members,
  rates,
  isLoading,
  isOwner,
}: MemberRatesSectionProps) => {
  const setRate = useSetProjectMemberRate(workspaceSlug, projectSlug);
  const ratesByUserId = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of rates) {
      const uid = r.attributes?.user_id;
      const cents = r.attributes?.hourly_rate_cents;
      if (uid && typeof cents === 'number') map.set(uid, cents);
    }
    return map;
  }, [rates]);

  return (
    <section className="space-y-3 rounded-xl border border-[var(--glass-card-border)] bg-[var(--glass-card-bg)] p-6 shadow-[var(--glass-card-shadow)] backdrop-blur-md">
      <header>
        <h2 className="text-foreground text-[14px] font-semibold tracking-[-0.005em]">Per-member rates</h2>
        <p className="text-foreground-faint text-[12.5px]">
          Override the default hourly rate for individual members.{' '}
          {isOwner ? null : 'Read-only — only the workspace owner can edit these.'}
        </p>
      </header>

      {isLoading ? (
        <p className="text-foreground-faint text-[12.5px]">Loading rates…</p>
      ) : members.length === 0 ? (
        <p className="text-foreground-faint text-[12.5px]">No workspace members.</p>
      ) : (
        <ul className="divide-y divide-[var(--glass-card-border)] rounded-xl border border-[var(--glass-card-border)] bg-[var(--glass-card-bg)] backdrop-blur-md">
          {members.map((m) => (
            <MemberRateRow
              key={m.id}
              member={m}
              currentRateCents={ratesByUserId.get(m.id) ?? null}
              isOwner={isOwner}
              onSet={(cents) =>
                setRate.mutateAsync({ userId: m.id, hourly_rate_cents: cents })
              }
              isSaving={setRate.isPending}
            />
          ))}
        </ul>
      )}
    </section>
  );
};

interface MemberRateRowProps {
  member: MemberItem;
  currentRateCents: number | null;
  isOwner: boolean;
  onSet: (cents: number | null) => Promise<unknown>;
  isSaving: boolean;
}

const MemberRateRow = ({ member, currentRateCents, isOwner, onSet, isSaving }: MemberRateRowProps) => {
  const [value, setValue] = useState(centsToInputValue(currentRateCents));
  const [error, setError] = useState<string | null>(null);

  // Reset the local value if the upstream rate changes (e.g. another tab).
  useEffect(() => {
    setValue(centsToInputValue(currentRateCents));
  }, [currentRateCents]);

  const handleCommit = async () => {
    setError(null);
    const next = inputValueToCents(value);
    if (next === currentRateCents) return;
    try {
      await onSet(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save rate.');
    }
  };

  const handleClear = async () => {
    setError(null);
    setValue('');
    try {
      await onSet(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not clear rate.');
    }
  };

  return (
    <li className="flex flex-wrap items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">
          {member.attributes?.name ?? member.attributes?.email ?? member.id}
        </p>
        {member.attributes?.role ? (
          <p className="text-foreground-faint text-[11.5px] capitalize">{member.attributes.role}</p>
        ) : null}
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-foreground-faint text-[12.5px]">$</span>
        <Input
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => void handleCommit()}
          disabled={!isOwner || isSaving}
          placeholder="—"
          className="w-24"
        />
        {isOwner && currentRateCents !== null ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => void handleClear()}
            disabled={isSaving}
            className="text-muted-foreground hover:text-foreground"
          >
            Clear
          </Button>
        ) : null}
      </div>
      {error ? (
        <p className="text-destructive w-full text-xs" role="alert">
          {error}
        </p>
      ) : null}
    </li>
  );
};

// ──────────────────────────────────────────────────────────────────────
//  Repository (slice 11) — engineering projects only
// ──────────────────────────────────────────────────────────────────────

interface RepositorySectionProps {
  workspaceSlug: string;
  projectSlug: string;
  currentOwner: string | null;
  currentName: string | null;
}

interface GithubRepoListPayload {
  data?: Array<{ owner: string; name: string; full_name: string }>;
}

/**
 * Repository card on engineering projects — picker + linked-state
 * display. Hidden entirely on generic projects (the parent page
 * gates the render).
 *
 * Picker uses shadcn Popover + Command (same pattern as TagPicker /
 * TaskParentPicker), which gives type-to-filter and full keyboard nav
 * for repo lists that get long. The repo list comes from
 * `useGithubRepositories` page-1 — for the v1 limit of 30 repos per
 * page that's plenty; if a connected org has >30 repos we'd add
 * "Load more" or server-side search later.
 *
 * If the org has no GitHub installation at all, we render a hint
 * pointing the user at workspace settings instead of a broken picker.
 */
const RepositorySection = ({
  workspaceSlug,
  projectSlug,
  currentOwner,
  currentName,
}: RepositorySectionProps) => {
  const installation = useGithubInstallation();
  const update = useUpdateProject(workspaceSlug, projectSlug);
  const [pickerOpen, setPickerOpen] = useState(false);

  const installed = Boolean(
    (installation.data as { data?: { id?: string } } | null | undefined)?.data?.id,
  );

  const repos = useGithubRepositories(1, 100);
  const repoList
    = (repos.data as GithubRepoListPayload | undefined)?.data ?? [];

  const isLinked = currentOwner !== null && currentName !== null;
  const fullName = isLinked ? `${currentOwner}/${currentName}` : null;

  const handleSelect = async (repo: { owner: string; name: string }): Promise<void> => {
    setPickerOpen(false);
    try {
      await update.mutateAsync({
        github_repo_owner: repo.owner,
        github_repo_name: repo.name,
      });
      toast.success(`Linked to ${repo.owner}/${repo.name}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not link repository.');
    }
  };

  const handleUnlink = async (): Promise<void> => {
    try {
      await update.mutateAsync({
        github_repo_owner: null,
        github_repo_name: null,
      });
      toast.success('Repository unlinked');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not unlink repository.');
    }
  };

  return (
    <section className="space-y-4 rounded-xl border border-[var(--glass-card-border)] bg-[var(--glass-card-bg)] p-6 shadow-[var(--glass-card-shadow)] backdrop-blur-md">
      <header>
        <h2 className="text-foreground text-[14px] font-semibold tracking-[-0.005em]">Repository</h2>
        <p className="text-foreground-faint text-[12.5px]">
          Link this engineering project to a GitHub repo so tasks can create branches.
        </p>
      </header>

      {! installed ? (
        <p className="text-foreground-faint text-[12.5px]">
          GitHub isn&apos;t connected for this workspace yet. Ask an owner to install it from{' '}
          <span className="font-medium">Workspace settings → GitHub</span>.
        </p>
      ) : isLinked ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3 rounded-md border border-[var(--glass-card-border)] bg-[var(--surface-quiet)] px-4 py-3 text-[13px]">
            <div className="min-w-0 flex-1 truncate font-mono">{fullName}</div>
            <a
              href={`https://github.com/${fullName}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground inline-flex shrink-0 items-center gap-1 text-xs"
            >
              <ExternalLink className="size-3" aria-hidden="true" />
              Open
            </a>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void handleUnlink()}
            disabled={update.isPending}
          >
            {update.isPending ? 'Unlinking…' : 'Unlink repository'}
          </Button>
        </div>
      ) : (
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline">
              Link a repository
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" sideOffset={6} className="w-80 p-0">
            <Command>
              <CommandInput placeholder="Search repositories…" className="h-9" />
              <CommandList>
                {repos.isLoading ? (
                  <p className="text-muted-foreground px-3 py-4 text-xs">Loading repos…</p>
                ) : repoList.length === 0 ? (
                  <p className="text-muted-foreground px-3 py-4 text-xs">
                    The installation can&apos;t see any repositories. Configure access on GitHub.
                  </p>
                ) : (
                  <>
                    <CommandEmpty>No repositories match.</CommandEmpty>
                    <CommandGroup>
                      {repoList.map((repo) => (
                        <CommandItem
                          key={repo.full_name}
                          value={repo.full_name}
                          onSelect={() => void handleSelect(repo)}
                          className="cursor-pointer font-mono text-xs"
                        >
                          <span className="flex-1 truncate">{repo.full_name}</span>
                          <Check className="size-3.5 opacity-0" aria-hidden="true" />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </section>
  );
};
